import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import DonateDialog from "@/components/DonateDialog";

/**
 * FundraiserPanel — shown on any event where `is_fundraiser === true`.
 *
 * Displays the beneficiary org name, a "$X raised of $Y goal" progress bar,
 * a Donate CTA, and the most recent supporters. Refetches the summary on
 * mount so returning donors see their own gift reflected immediately.
 */
export default function FundraiserPanel({ event }) {
  const [summary, setSummary] = useState(null);
  const [showDonate, setShowDonate] = useState(false);

  useEffect(() => {
    if (!event?.is_fundraiser) return;
    api.get(`/events/${event.id}/donations/summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => {});
  }, [event?.id, event?.is_fundraiser]);

  if (!event?.is_fundraiser) return null;

  const raised = (summary?.raised_cents ?? event.fundraising_raised_cents ?? 0);
  const goal = summary?.goal_cents ?? event.fundraising_goal_cents;
  const donors = summary?.donor_count ?? event.fundraising_donor_count ?? 0;
  const pct = goal ? Math.min(100, Math.round((raised / goal) * 100)) : null;
  const orgName = summary?.beneficiary_org_name || event.beneficiary_org_name || "the nonprofit";

  return (
    <div
      data-testid="fundraiser-panel"
      className="mt-8 rounded-xl p-6 border-2"
      style={{ borderColor: "var(--avt-pink)", backgroundColor: "rgba(236,72,153,0.06)" }}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full"
               style={{ backgroundColor: "rgba(236,72,153,0.14)", color: "var(--avt-pink)", border: "1.5px solid var(--avt-pink)" }}>
            <Heart className="h-3 w-3" fill="var(--avt-pink)" strokeWidth={0} /> Fundraiser event
          </div>
          <div className="mt-2 font-serif text-xl" data-testid="fundraiser-beneficiary">
            Raising for {orgName}
          </div>
          <p className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
            100% of every donation goes to {orgName}. AddVendTour takes 0%.
          </p>
        </div>
        <Button
          data-testid="fundraiser-donate-btn"
          onClick={() => setShowDonate(true)}
          className="btn-primary rounded-full px-6"
        >
          <Heart className="h-4 w-4 mr-2" /> Donate now
        </Button>
      </div>

      {/* Progress */}
      <div className="mt-5 space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="font-serif text-3xl" style={{ color: "var(--avt-purple)" }} data-testid="fundraiser-raised">
            ${(raised / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
          <span className="text-xs" style={{ color: "var(--ink-soft)" }}>
            {donors} {donors === 1 ? "supporter" : "supporters"}
            {goal ? <> · goal <strong>${(goal / 100).toLocaleString()}</strong></> : null}
          </span>
        </div>
        {goal ? (
          <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0,0,0,0.06)" }}>
            <div
              data-testid="fundraiser-progress-bar"
              style={{
                width: `${pct}%`,
                height: "100%",
                background: "linear-gradient(90deg, var(--avt-pink), var(--avt-purple))",
                transition: "width 400ms ease",
              }}
            />
          </div>
        ) : null}
        {pct !== null && (
          <div className="text-xs text-right" style={{ color: "var(--ink-soft)" }}>
            {pct}% of goal
          </div>
        )}
      </div>

      {/* Recent supporters */}
      {summary?.recent?.length > 0 && (
        <div className="mt-5 pt-4 border-t" style={{ borderColor: "rgba(236,72,153,0.2)" }} data-testid="fundraiser-recent">
          <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--ink-soft)" }}>
            Recent supporters
          </div>
          <ul className="space-y-2">
            {summary.recent.slice(0, 5).map((d, i) => (
              <li key={i} className="flex items-start gap-3 text-sm">
                <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0"
                     style={{ backgroundColor: "rgba(107,47,186,0.10)", color: "var(--avt-purple)" }}>
                  <Heart className="h-3.5 w-3.5" fill="currentColor" strokeWidth={0} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-semibold truncate">{d.donor_name}</span>
                    <span className="text-xs" style={{ color: "var(--avt-purple)" }}>
                      ${(d.amount_cents / 100).toFixed(0)}
                    </span>
                  </div>
                  {d.message && (
                    <p className="text-xs italic" style={{ color: "var(--ink-soft)" }}>
                      &ldquo;{d.message}&rdquo;
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <DonateDialog
        open={showDonate}
        onOpenChange={setShowDonate}
        nonprofitUserId={event.beneficiary_user_id}
        orgName={orgName}
        eventId={event.id}
      />
    </div>
  );
}
